/*
 * Paleta del tablero, tomada de las marcas y validada con validate_palette.js
 * de la guía dataviz (CVD y visión normal sobre la tarjeta blanca).
 *
 * - Estados: colores secundarios del manual de marca de SoyMomo (Brand
 *   guidelines, pág. 6). El manual no trae rojo, así que Terminada usa
 *   #dc2626, el rojo que mejor se separa del verde de Activa con daltonismo.
 * - Operadores y pasarelas: el color principal de cada empresa, sacado de su
 *   sitio o su logo (septiembre 2026). Entel y Telefónica Tech son casi el
 *   mismo azul, así que el color nunca va solo: siempre acompaña al nombre.
 * - "Sin cobro" no es un color: es el riel vacío de la barra.
 */

export const BRAND = '#603BB0';

/** Colores secundarios del manual de marca de SoyMomo, uso digital. */
const SOYMOMO_SECONDARY = {
    azul: '#0278fc',
    cian: '#00bbd3',
    amarillo: '#fbc353',
    rosa: '#f4639f',
    lavanda: '#8d84dc',
    verde: '#00bb96',
};

/** Riel vacío de las barras. */
export const TRACK = '#f3f0f9';

export const STATUS_THEME = {
    ACTIVATED: { label: 'Activa', color: SOYMOMO_SECONDARY.verde },
    ACTIVATION_PENDING: { label: 'Pendiente', color: SOYMOMO_SECONDARY.lavanda },
    // Morado de impresión del manual (pág. 5): el cian y el rosa secundarios
    // se confunden con el verde de Activa, y el azul con los operadores.
    'PRE-ACTIVATED': { label: 'Preactivada', color: '#5e398e' },
    SUSPENDED: { label: 'Suspendida', color: SOYMOMO_SECONDARY.amarillo },
    TERMINATED: { label: 'Terminada', color: '#dc2626' },
    DEACTIVATED: { label: 'Desactivada', color: '#898781' },
    '(vacío)': { label: 'Sin estado', color: '#52514e' },
};

/**
 * Activas primero: la barra se lee como “qué tan sano está el parque”. El
 * orden es parte de la separación de colores: los vecinos se validaron así.
 */
export const STATUS_ORDER = [
    'ACTIVATED',
    'ACTIVATION_PENDING',
    'PRE-ACTIVATED',
    'SUSPENDED',
    'TERMINATED',
    'DEACTIVATED',
    '(vacío)',
];

export const PAYMENT_THEME = {
    // Marino del logo de apio.cl.
    apio: { label: 'Apio', color: '#141685' },
    // Violeta de los recursos de marca de stripe.com.
    stripe: { label: 'Stripe', color: '#533afd' },
    ninguno: { label: 'Sin cobro', color: TRACK, isTrack: true },
};

/** Azul de marca de whereversim.com: lo comparten sus dos países, siempre con el país al lado. */
const WHEREVER_BLUE = '#009dff';

/**
 * Color principal de cada operador, por su nombre en el tablero. Wherever se
 * muestra por país, igual que en el backend (SPLIT_BY_COUNTRY).
 */
const OPERATOR_COLORS = {
    // --color-primary de entel.cl
    ENTEL: '#002eff',
    // Tono medio del degradado del logo de easym2m.eu
    EASYM2M: '#f36e22',
    'WHEREVER SE': WHEREVER_BLUE,
    'WHEREVER DE': WHEREVER_BLUE,
    // theme-color y logo de telefonicatech.com
    TELEFONICATECH: '#0066ff',
};

/** Un operador sin color registrado va en neutro, nunca con un color inventado. */
export const UNKNOWN_OPERATOR_COLOR = '#94a3b8';

/** Nombre de operador → clave estable: sin tildes, espacios ni guiones, en mayúsculas. */
export function operatorKey(name) {
    return (name || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[\s_-]/g, '')
        .toUpperCase();
}

/** Operadores con color de marca registrado, que son también los que tienen página de detalle. */
export const BRANDED_OPERATORS = Object.keys(OPERATOR_COLORS);

/** El nombre registrado que corresponde a `name`, sin importar mayúsculas, tildes ni espacios. */
export function brandedOperator(name) {
    const key = operatorKey(name);
    return BRANDED_OPERATORS.find((operator) => operatorKey(operator) === key) || null;
}

export function operatorColor(name) {
    const operator = brandedOperator(name);
    return operator ? OPERATOR_COLORS[operator] : UNKNOWN_OPERATOR_COLOR;
}

/**
 * Filtro de la lista que no es un estado: terminadas con la baja de la línea
 * aún por venir. Igual que PENDING_DEACTIVATION en el backend.
 */
export const PENDING_DEACTIVATION = 'PENDING_DEACTIVATION';

/** Son terminadas: comparte el rojo de Terminada, por eso no va entre los estados. */
const PENDING_DEACTIVATION_THEME = {
    label: 'Con baja programada',
    color: STATUS_THEME.TERMINATED.color,
};

export function statusTheme(status) {
    if (status === PENDING_DEACTIVATION) return PENDING_DEACTIVATION_THEME;
    return (
        STATUS_THEME[status] || {
            label: status || 'Sin estado',
            color: STATUS_THEME['(vacío)'].color,
        }
    );
}

const PAYMENT_STATUS = {
    active: ['Al día', SOYMOMO_SECONDARY.verde],
    completed: ['Completada', SOYMOMO_SECONDARY.verde],
    trialing: ['En prueba', SOYMOMO_SECONDARY.verde],
    pending: ['Pendiente', SOYMOMO_SECONDARY.lavanda],
    incomplete: ['Incompleta', SOYMOMO_SECONDARY.lavanda],
    past_due: ['Pago atrasado', SOYMOMO_SECONDARY.amarillo],
    failed: ['Cobro fallido', '#dc2626'],
    unpaid: ['Impaga', '#dc2626'],
    paused: ['Pausada', '#898781'],
    canceled: ['Cancelada', '#898781'],
};

/**
 * Estado del cobro en Apio o Stripe, en palabras del tablero. Apio cuenta los
 * reintentos en el propio estado ("failed attempt 2").
 */
export function paymentStatusTheme(provider, status) {
    if (!provider) return { label: 'Sin cobro', color: '#898781' };
    const value = (status || '').trim().toLowerCase();
    const attempt = value.match(/^failed attempt (\d+)$/);
    if (attempt) {
        return { label: `Intento fallido ${attempt[1]}`, color: SOYMOMO_SECONDARY.amarillo };
    }
    const [label, color] = PAYMENT_STATUS[value] || [status || 'Sin estado', '#898781'];
    return { label, color };
}

export function formatCount(value) {
    return (value || 0).toLocaleString('es-CL');
}

export function formatShare(part, total) {
    if (!total) return '0%';
    const percent = (part / total) * 100;
    if (percent > 0 && percent < 1) return '<1%';
    return `${Math.round(percent)}%`;
}
